import { getPlayerAttendanceHistory, getPlayerAttendanceStat } from '@/features/attendance/selectors';
import { getPlayerProgression } from '@/features/progression/logic';
import type { GuildState } from '@/types/app-state';
import type { PlayerSheetAttendanceEntry, Profile, RoleCategory } from '@/types/domain';
import { getProfileInGameName } from '@/utils/profile';

export interface PlayerSheetData {
  profile: Profile;
  inGameName: string;
  totalSignupRecords: number;
  attendanceCount: number;
  progressionLevel: number;
  progressionTier: string;
  isManualProgressionOverride: boolean;
  lastAttendedAt?: string;
  lastAttendedMassTitle?: string;
  weaponUsage: Array<{
    weaponName: string;
    usageCount: number;
  }>;
  roleUsage: Array<{
    roleCategory: RoleCategory;
    usageCount: number;
  }>;
  recentAttendance: PlayerSheetAttendanceEntry[];
}

export function getPlayerSheetData(state: GuildState, profileId: string): PlayerSheetData | null {
  const profile = state.profiles.find((entry) => entry.id === profileId);

  if (!profile) {
    return null;
  }

  const attendanceStats = getPlayerAttendanceStat(state, profileId);
  const eventsById = new Map(state.events.map((event) => [event.id, event]));
  const attendanceRecords = getPlayerAttendanceHistory(state, profileId)
    .map((entry) => ({
      entry,
      event: eventsById.get(entry.eventId),
    }))
    .sort((left, right) => {
      const rightTime = new Date(right.event?.startsAt ?? right.entry.attendanceLockedAt).getTime();
      const leftTime = new Date(left.event?.startsAt ?? left.entry.attendanceLockedAt).getTime();
      return rightTime - leftTime;
    });

  const progression = getPlayerProgression(profile, attendanceStats?.totalCountedMasses ?? 0);

  return {
    profile,
    inGameName: getProfileInGameName(profile),
    totalSignupRecords: attendanceStats?.totalSignupRecords ?? state.signups.filter((entry) => entry.profileId === profileId).length,
    attendanceCount: attendanceStats?.totalCountedMasses ?? 0,
    progressionLevel: progression.level,
    progressionTier: progression.tier,
    isManualProgressionOverride: progression.isManualOverride,
    lastAttendedAt: attendanceStats?.lastAttendedAt,
    lastAttendedMassTitle: attendanceStats?.lastAttendedMassTitle,
    weaponUsage: attendanceStats?.weaponUsage ?? [],
    roleUsage: attendanceStats?.roleUsage ?? [],
    recentAttendance: attendanceRecords.slice(0, 6).map(({ entry, event }) => ({
      eventId: event?.id ?? entry.eventId,
      eventTitle: event?.title ?? 'Archived Mass',
      startsAt: event?.startsAt ?? entry.attendanceLockedAt,
      weaponName: entry.weaponNameSnapshot || 'Unknown Weapon',
      roleCategory: entry.roleCategorySnapshot || ('support' as RoleCategory),
      partyNumber: entry.partyNumberSnapshot,
    })),
  };
}
